// pages/EinkDesigner/index.jsx
// E-Ink Display Designer — visual editor for Waveshare 2.7" e-paper displays (264×176)
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Select, MenuItem,
  FormControl, InputLabel, Checkbox, FormControlLabel, Paper, Divider,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Stack, CircularProgress, Alert, Tabs, Tab, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon,
  Publish as PushIcon, TextFields as TextIcon, CropSquare as RectIcon,
  HorizontalRule as LineIcon, RadioButtonUnchecked as CircleIcon,
  Image as ImageIcon, DataObject as JsonIcon, Refresh as RefreshIcon,
  ArrowUpward, ArrowDownward, ContentCopy, Close as CloseIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { einkDisplayApi, deviceTypesApi } from '../../services/apiClient';
import { useThemeSettings } from '../../contexts/ThemeContext';

// ─── Constants ─────────────────────────────────────────────────────
const DW = 264, DH = 176; // native display resolution
const SCALE = 3;           // zoom factor
const CW = DW * SCALE, CH = DH * SCALE;
const SNAP = 2;

// Predefined variables grouped by source service
const VAR_GROUPS = {
  time: [
    'time','time_s','date','date_us','year','month','month_name','day',
    'weekday','hour','minute','second',
  ],
  system: [
    'ip','hostname','wifi_db','wifi_ssid','cpu_temp','uptime',
    'mem_total','mem_used','mem_free','mac','cpu','mem','disk','internet',
  ],
  sensor: [
    'temperature','humidity',
  ],
  device: [
    'model','version','serial','cpu_arch','cpu_cores','memory_total',
  ],
};

const BUILTIN_VARS = Object.values(VAR_GROUPS).flat();

const ICON_NAMES = [
  'wifi','battery','check','cross','heart','star','clock','mail',
  'home','gear','sun','cloud','warning','signal','bell','power',
  'lightning','arrow-up','arrow-down',
];

// ─── Icon Drawing Functions (matches display.py exactly) ──────────
const ICON_DRAW = {
  wifi(ctx, s) {
    const cx = s/2, cy = s - 2;
    ctx.lineWidth = Math.max(1, s/10);
    [0.9, 0.6, 0.3].forEach(f => {
      ctx.beginPath();
      ctx.arc(cx, cy, s*f, 210*Math.PI/180, 330*Math.PI/180);
      ctx.stroke();
    });
    ctx.beginPath(); ctx.arc(cx, cy, Math.max(1, s/8), 0, Math.PI*2); ctx.fill();
  },
  battery(ctx, s) {
    const bw = s*0.8, bh = s*0.5, by = (s-bh)/2;
    ctx.lineWidth = Math.max(1, s/12);
    ctx.strokeRect(0, by, bw, bh);
    ctx.fillRect(bw, by+(bh-bh/3)/2, 3, bh/3);
    ctx.fillRect(3, by+3, bw*0.6, bh-6);
  },
  check(ctx, s) {
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath();
    ctx.moveTo(s*0.15, s*0.5); ctx.lineTo(s*0.4, s*0.75); ctx.lineTo(s*0.85, s*0.2);
    ctx.stroke();
  },
  cross(ctx, s) {
    ctx.lineWidth = Math.max(2, s/8);
    const m = s*0.2;
    ctx.beginPath(); ctx.moveTo(m, m); ctx.lineTo(s-m, s-m); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s-m, m); ctx.lineTo(m, s-m); ctx.stroke();
  },
  heart(ctx, s) {
    ctx.beginPath();
    for (let t = 0; t <= 100; t++) {
      const a = Math.PI*2*t/100;
      const px = 16*Math.pow(Math.sin(a), 3);
      const py = -(13*Math.cos(a)-5*Math.cos(2*a)-2*Math.cos(3*a)-Math.cos(4*a));
      const x = s/2+px*s/36, y = s/2+py*s/36-s*0.1;
      t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill();
  },
  star(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.45;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = Math.PI/2 + Math.PI*2*i/10;
      const cr = i%2 === 0 ? r : r*0.4;
      const x = cx+cr*Math.cos(a), y = cy-cr*Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fill();
  },
  clock(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.42;
    ctx.lineWidth = Math.max(1, s/12);
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI*2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx, cy-r*0.65); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx+r*0.5, cy+r*0.2); ctx.stroke();
  },
  mail(ctx, s) {
    ctx.lineWidth = Math.max(1, s/12);
    const m = s*0.1, ey = s*0.25;
    ctx.strokeRect(m, ey, s-2*m, s-2*ey);
    ctx.beginPath(); ctx.moveTo(m, ey); ctx.lineTo(s/2, s*0.55); ctx.lineTo(s-m, ey); ctx.stroke();
  },
  home(ctx, s) {
    ctx.lineWidth = Math.max(1, s/10);
    const cx = s/2;
    ctx.beginPath();
    ctx.moveTo(cx, s*0.1); ctx.lineTo(s*0.1, s*0.5); ctx.lineTo(s*0.9, s*0.5); ctx.closePath();
    ctx.stroke();
    ctx.strokeRect(s*0.25, s*0.5, s*0.5, s*0.4);
    ctx.fillRect(cx-s*0.12, s*0.6, s*0.24, s*0.3);
  },
  gear(ctx, s) {
    const cx = s/2, cy = s/2, r1 = s*0.4, r2 = s*0.25;
    ctx.lineWidth = Math.max(1, s/10);
    ctx.beginPath(); ctx.arc(cx, cy, r2, 0, Math.PI*2); ctx.stroke();
    ctx.lineWidth = Math.max(2, s*0.12);
    for (let i = 0; i < 8; i++) {
      const a = Math.PI*2*i/8;
      ctx.beginPath();
      ctx.moveTo(cx+r2*Math.cos(a), cy+r2*Math.sin(a));
      ctx.lineTo(cx+r1*Math.cos(a), cy+r1*Math.sin(a));
      ctx.stroke();
    }
  },
  sun(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.2;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI*2); ctx.fill();
    ctx.lineWidth = Math.max(1, s/12);
    for (let i = 0; i < 8; i++) {
      const a = Math.PI*2*i/8;
      ctx.beginPath();
      ctx.moveTo(cx+r*1.4*Math.cos(a), cy+r*1.4*Math.sin(a));
      ctx.lineTo(cx+r*2.2*Math.cos(a), cy+r*2.2*Math.sin(a));
      ctx.stroke();
    }
  },
  cloud(ctx, s) {
    const fe = (x1,y1,x2,y2) => {
      ctx.beginPath();
      ctx.ellipse((x1+x2)/2,(y1+y2)/2,(x2-x1)/2,(y2-y1)/2,0,0,Math.PI*2);
      ctx.fill();
    };
    fe(s*0.1,s*0.3,s*0.5,s*0.7);
    fe(s*0.3,s*0.15,s*0.7,s*0.6);
    fe(s*0.5,s*0.25,s*0.9,s*0.7);
    ctx.fillRect(s*0.15, s*0.5, s*0.7, s*0.2);
  },
  warning(ctx, s) {
    ctx.lineWidth = Math.max(2, s/8);
    const cx = s/2;
    ctx.beginPath();
    ctx.moveTo(cx, s*0.08); ctx.lineTo(s*0.08, s*0.88); ctx.lineTo(s*0.92, s*0.88); ctx.closePath();
    ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, s*0.35); ctx.lineTo(cx, s*0.6); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, s*0.7, Math.max(1, s*0.04), 0, Math.PI*2); ctx.fill();
  },
  signal(ctx, s) {
    const bars = 4, bw = s*0.15;
    const gap = (s - bars*bw) / (bars+1);
    for (let i = 0; i < bars; i++) {
      const bh = s*0.3 + s*0.6*(i+1)/bars;
      ctx.fillRect(gap + i*(bw+gap), s-bh, bw, bh);
    }
  },
  bell(ctx, s) {
    const cx = s/2;
    ctx.lineWidth = Math.max(1, s/12);
    ctx.beginPath();
    ctx.ellipse(s*0.5, (s*0.1+s*0.65)/2, (s*0.8-s*0.2)/2, (s*0.65-s*0.1)/2, 0, Math.PI, 0);
    ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.2, s*0.55); ctx.lineTo(s*0.2, s*0.72); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.8, s*0.55); ctx.lineTo(s*0.8, s*0.72); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.12, s*0.72); ctx.lineTo(s*0.88, s*0.72); ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(cx, (s*0.78+s*0.92)/2, s*0.07, (s*0.92-s*0.78)/2, 0, 0, Math.PI*2);
    ctx.fill();
  },
  power(ctx, s) {
    const cx = s/2, cy = s/2, r = s*0.38;
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath(); ctx.arc(cx, cy, r, 40*Math.PI/180, 320*Math.PI/180); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, s*0.12); ctx.lineTo(cx, cy); ctx.stroke();
  },
  lightning(ctx, s) {
    const pts = [[s*0.55,s*0.05],[s*0.2,s*0.5],[s*0.48,s*0.5],[s*0.35,s*0.95],[s*0.8,s*0.4],[s*0.52,s*0.4]];
    ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath(); ctx.fill();
  },
  'arrow-up'(ctx, s) {
    const cx = s/2;
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath(); ctx.moveTo(cx, s*0.15); ctx.lineTo(cx, s*0.85); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.25, s*0.4); ctx.lineTo(cx, s*0.15); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.75, s*0.4); ctx.lineTo(cx, s*0.15); ctx.stroke();
  },
  'arrow-down'(ctx, s) {
    const cx = s/2;
    ctx.lineWidth = Math.max(2, s/8);
    ctx.beginPath(); ctx.moveTo(cx, s*0.15); ctx.lineTo(cx, s*0.85); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.25, s*0.6); ctx.lineTo(cx, s*0.85); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s*0.75, s*0.6); ctx.lineTo(cx, s*0.85); ctx.stroke();
  },
};

// ─── Utility Functions ────────────────────────────────────────────
const iconCache = {};
function renderIconToDataURL(name, size) {
  const key = `${name}_${size}`;
  if (iconCache[key]) return iconCache[key];
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#000'; ctx.strokeStyle = '#000';
  const fn = ICON_DRAW[name];
  if (fn) fn(ctx, size);
  iconCache[key] = c.toDataURL();
  return iconCache[key];
}

function iconToPixels(name, size) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#000'; ctx.strokeStyle = '#000';
  const fn = ICON_DRAW[name];
  if (fn) fn(ctx, size);
  const imgData = ctx.getImageData(0, 0, size, size);
  const rows = [];
  for (let y = 0; y < size; y++) {
    let row = '';
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const lum = imgData.data[i]*0.299 + imgData.data[i+1]*0.587 + imgData.data[i+2]*0.114;
      row += lum < 128 ? '1' : '0';
    }
    rows.push(row);
  }
  return rows;
}

function imageToPixels(dataUrl, width, height, inverted) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = width; c.height = height;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const imgData = ctx.getImageData(0, 0, width, height);
      const rows = [];
      for (let y = 0; y < height; y++) {
        let row = '';
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const lum = imgData.data[i]*0.299 + imgData.data[i+1]*0.587 + imgData.data[i+2]*0.114;
          let black = lum < 128;
          if (inverted) black = !black;
          row += black ? '1' : '0';
        }
        rows.push(row);
      }
      resolve(rows);
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

function getLocalTimeVars() {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return {
    time: pad(now.getHours()) + ':' + pad(now.getMinutes()),
    time_s: pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':' + pad(now.getSeconds()),
    date: pad(now.getDate()) + '-' + pad(now.getMonth()+1) + '-' + now.getFullYear(),
    date_us: pad(now.getMonth()+1) + '/' + pad(now.getDate()) + '/' + now.getFullYear(),
    year: String(now.getFullYear()),
    month: pad(now.getMonth()+1),
    month_name: months[now.getMonth()],
    day: pad(now.getDate()),
    weekday: days[now.getDay()],
    hour: pad(now.getHours()),
    minute: pad(now.getMinutes()),
    second: pad(now.getSeconds()),
  };
}

function resolveVars(text, variables) {
  const timeVars = getLocalTimeVars();
  return text.replace(/\{\{(\w+)\}\}/g, (m, name) => {
    if (timeVars[name] !== undefined) return timeVars[name];
    return variables[name] !== undefined ? variables[name] : m;
  });
}

function detectVariables(pages) {
  const vars = new Set();
  for (const p of pages) {
    for (const el of p.elements) {
      if ((el.type === 'text' || el.type === 'json') && (el.text || el.data)) {
        const str = el.text || el.data || '';
        let match;
        const re = /\{\{(\w+)\}\}/g;
        while ((match = re.exec(str)) !== null) vars.add(match[1]);
      }
    }
  }
  return Array.from(vars).sort();
}

let nextId = 1;

// ─── Main Component ───────────────────────────────────────────────
export default function EinkDesigner() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useThemeSettings();

  // Device type selection
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Designer state
  const [pages, setPages] = useState([{ name: 'Page 1', elements: [] }]);
  const [curPage, setCurPage] = useState(0);
  const [selId, setSelId] = useState(null);
  const [variables, setVariables] = useState({});
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Button config dialog
  const [buttonConfigPage, setButtonConfigPage] = useState(null);

  // Pixel editor
  const [pixelEditorOpen, setPixelEditorOpen] = useState(false);
  const [pixelEditorEl, setPixelEditorEl] = useState(null);
  const [pixelEditorInitPixels, setPixelEditorInitPixels] = useState(null);

  // Drag state
  const dragRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const imgCacheRef = useRef({});

  // Memoized helpers
  const curElements = useCallback(() => pages[curPage]?.elements || [], [pages, curPage]);
  const getEl = useCallback((id) => curElements().find(e => e.id === id), [curElements]);

  // ─── Load device types ─────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await deviceTypesApi.getAll({ limit: 1000 });
        const allTypes = res.data?.deviceTypes || res.data?.data || res.data || [];
        setDeviceTypes(allTypes);
      } catch (err) {
        enqueueSnackbar(t('EinkDesigner.load_device_types_failed'), { variant: 'error' });
      } finally {
        setLoadingTypes(false);
      }
    })();
  }, []);

  // ─── Load template when device type changes ────────────────────
  useEffect(() => {
    if (!selectedType) return;
    (async () => {
      try {
        const res = await einkDisplayApi.getByDeviceType(selectedType.id);
        const tmpl = res.data?.template;
        if (tmpl && tmpl.pages?.length) {
          const loadedPages = tmpl.pages.map(p => ({
            name: p.name,
            elements: (p.elements || []).map(el => {
              if (!el.id) el.id = 'e' + (nextId++);
              else {
                const n = parseInt(el.id.slice(1));
                if (n >= nextId) nextId = n + 1;
              }
              return el;
            }),
          }));
          setPages(loadedPages);
          setCurPage(tmpl.currentPage || 0);
        } else {
          setPages([{ name: 'Page 1', elements: [] }]);
          setCurPage(0);
        }
        setVersion(tmpl?.version || 0);
        setVariables(tmpl?.variables || {});
        setSelId(null);
        setDirty(false);
      } catch (err) {
        console.error('Failed to load e-ink template:', err);
      }
    })();
  }, [selectedType]);

  // ─── Refresh time variables periodically ────────────────────────
  // (No device-level variable polling needed; template vars are managed in the designer)

  // ─── Canvas tick for time variables ────────────────────────────
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const usedVars = detectVariables(pages);
    const hasTime = usedVars.some(v => ['time','time_s','second','minute','hour'].includes(v));
    if (!hasTime) return;
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [pages]);

  // ─── Canvas rendering ──────────────────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');

    // White background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, DW, DH);
    ctx.fillStyle = '#000';
    ctx.strokeStyle = '#000';

    const elements = curElements();
    for (const el of elements) {
      const x = el.x || 0, y = el.y || 0;

      if (el.type === 'text') {
        const sz = el.size || 16;
        const bold = el.bold ? 'bold ' : '';
        ctx.font = `${bold}${sz}px DejaVu Sans, sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#000';
        ctx.fillText(resolveVars(el.text || '', variables), x, y);

      } else if (el.type === 'icon') {
        const isz = el.size || 20;
        if (el.pixels?.length) {
          ctx.fillStyle = '#000';
          for (let row = 0; row < el.pixels.length; row++)
            for (let col = 0; col < el.pixels[row].length; col++)
              if (el.pixels[row][col] === '1') ctx.fillRect(x + col, y + row, 1, 1);
        } else {
          ctx.save();
          ctx.translate(x, y);
          ctx.fillStyle = '#000';
          ctx.strokeStyle = '#000';
          const fn = ICON_DRAW[el.name];
          if (fn) fn(ctx, isz);
          ctx.restore();
        }

      } else if (el.type === 'rect') {
        const rw = el.width || 50, rh = el.height || 30;
        ctx.lineWidth = Math.max(1, el.strokeWidth || 2);
        if (el.filled) {
          ctx.fillStyle = '#000'; ctx.fillRect(x, y, rw, rh);
        } else {
          ctx.strokeStyle = '#000'; ctx.strokeRect(x, y, rw, rh);
        }

      } else if (el.type === 'circle') {
        const cr = el.radius || 20;
        ctx.lineWidth = Math.max(1, el.strokeWidth || 2);
        ctx.beginPath(); ctx.arc(x, y, cr, 0, Math.PI * 2);
        if (el.filled) { ctx.fillStyle = '#000'; ctx.fill(); }
        else { ctx.strokeStyle = '#000'; ctx.stroke(); }

      } else if (el.type === 'line') {
        const lx2 = el.x2 ?? x + 50, ly2 = el.y2 ?? y;
        ctx.lineWidth = Math.max(1, el.strokeWidth || 2);
        ctx.strokeStyle = '#000';
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(lx2, ly2); ctx.stroke();

      } else if (el.type === 'image') {
        const iw = el.width || 60, ih = el.height || 60;
        if (el.pixels?.length) {
          ctx.fillStyle = '#000';
          for (let row = 0; row < el.pixels.length; row++)
            for (let col = 0; col < el.pixels[row].length; col++)
              if (el.pixels[row][col] === '1') ctx.fillRect(x + col, y + row, 1, 1);
        } else if (el.data) {
          let cached = imgCacheRef.current[el.data];
          if (!cached) {
            const img = new Image();
            img.onload = () => { imgCacheRef.current[el.data] = img; setTick(t => t + 1); };
            img.src = el.data;
            imgCacheRef.current[el.data] = 'loading';
          } else if (cached !== 'loading') {
            const tc = document.createElement('canvas');
            tc.width = iw; tc.height = ih;
            const tctx = tc.getContext('2d');
            tctx.drawImage(cached, 0, 0, iw, ih);
            const td = tctx.getImageData(0, 0, iw, ih);
            for (let pi = 0; pi < td.data.length; pi += 4) {
              const lum = td.data[pi]*0.299 + td.data[pi+1]*0.587 + td.data[pi+2]*0.114;
              let bw = lum < 128 ? 0 : 255;
              if (el.inverted) bw = 255 - bw;
              td.data[pi] = td.data[pi+1] = td.data[pi+2] = bw;
              td.data[pi+3] = 255;
            }
            tctx.putImageData(td, 0, 0);
            ctx.drawImage(tc, x, y);
          }
        }

      } else if (el.type === 'json') {
        const jw = el.width || 120, jh = el.height || 80;
        let fontSize = el.size || 12;
        const jbold = el.bold ? 'bold ' : '';
        const jsonStr = resolveVars(el.data || '{}', variables);
        let entries = [];
        try {
          const obj = JSON.parse(jsonStr);
          for (const jk of Object.keys(obj)) entries.push({ k: jk, v: String(obj[jk]) });
        } catch { entries = [{ k: 'error', v: 'Invalid JSON' }]; }

        // Auto-scale
        let lineH;
        while (fontSize > 6) {
          ctx.font = `${jbold}${fontSize}px DejaVu Sans, sans-serif`;
          lineH = Math.ceil(fontSize * 1.3);
          if ((entries.length * lineH) <= jh) break;
          fontSize--;
        }
        lineH = Math.ceil(fontSize * 1.3);

        ctx.textBaseline = 'top';
        ctx.fillStyle = '#000';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, jw, jh);
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, jw, jh); ctx.clip();
        for (let ji = 0; ji < entries.length; ji++) {
          const jy = y + 2 + ji * lineH;
          if (jy + lineH > y + jh + lineH) break;
          const keyStr = entries[ji].k + ': ';
          ctx.font = `bold ${fontSize}px DejaVu Sans, sans-serif`;
          ctx.fillText(keyStr, x + 3, jy);
          const keyW = ctx.measureText(keyStr).width;
          ctx.font = `${fontSize}px DejaVu Sans, sans-serif`;
          ctx.fillText(entries[ji].v, x + 3 + keyW, jy);
        }
        ctx.restore();
      }
    }

    // Convert to 1-bit (matches e-paper)
    const imgData = ctx.getImageData(0, 0, DW, DH);
    for (let i = 0; i < imgData.data.length; i += 4) {
      const l = imgData.data[i]*0.299 + imgData.data[i+1]*0.587 + imgData.data[i+2]*0.114;
      const v = l < 128 ? 0 : 255;
      imgData.data[i] = imgData.data[i+1] = imgData.data[i+2] = v;
    }
    ctx.putImageData(imgData, 0, 0);
  }, [pages, curPage, selId, variables, tick]);

  // ─── Hit-test data for overlay ─────────────────────────────────
  const hitBoxes = useMemo(() => {
    const boxes = [];
    const elements = pages[curPage]?.elements || [];
    for (const el of elements) {
      const x = el.x || 0, y = el.y || 0;
      if (el.type === 'text') {
        const sz = el.size || 16;
        const tw = Math.max(20, (el.text || '').length * sz * 0.6);
        boxes.push({ id: el.id, x, y, w: tw, h: sz * 1.2 });
      } else if (el.type === 'icon') {
        const isz = el.size || 20;
        boxes.push({ id: el.id, x, y, w: isz, h: isz });
      } else if (el.type === 'rect') {
        boxes.push({ id: el.id, x, y, w: el.width || 50, h: el.height || 30, resizable: true });
      } else if (el.type === 'circle') {
        const cr = el.radius || 20;
        boxes.push({ id: el.id, x: x - cr, y: y - cr, w: cr*2, h: cr*2 });
      } else if (el.type === 'line') {
        const x2 = el.x2 ?? x+50, y2 = el.y2 ?? y;
        const minX = Math.min(x, x2) - 4, minY = Math.min(y, y2) - 4;
        const maxX = Math.max(x, x2) + 4, maxY = Math.max(y, y2) + 4;
        boxes.push({ id: el.id, x: minX, y: minY, w: maxX-minX, h: maxY-minY, isLine: true });
      } else if (el.type === 'image') {
        boxes.push({ id: el.id, x, y, w: el.width || 60, h: el.height || 60, resizable: true });
      } else if (el.type === 'json') {
        boxes.push({ id: el.id, x, y, w: el.width || 120, h: el.height || 80, resizable: true });
      }
    }
    return boxes;
  }, [pages, curPage, tick]);

  // ─── Mutation helper ───────────────────────────────────────────
  const updatePages = useCallback((fn) => {
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      fn(next);
      return next;
    });
    setDirty(true);
  }, []);

  const updateElement = useCallback((id, updates) => {
    updatePages(pages => {
      const el = pages[curPage]?.elements?.find(e => e.id === id);
      if (el) Object.assign(el, updates);
    });
  }, [curPage, updatePages]);

  // ─── Add elements ──────────────────────────────────────────────
  const addElement = useCallback((type, extra = {}) => {
    const id = 'e' + (nextId++);
    const base = { id, type, x: Math.round(DW/2 - 25), y: Math.round(DH/2 - 10) };
    const defs = {
      text: { text: 'Hello', size: 18, bold: false },
      icon: { name: 'wifi', size: 24 },
      rect: { width: 60, height: 30, filled: false, strokeWidth: 2 },
      line: { x2: base.x + 80, y2: base.y, strokeWidth: 2 },
      circle: { radius: 20, filled: false, strokeWidth: 2, x: DW/2, y: DH/2 },
      image: { data: '', width: 60, height: 60 },
      json: { width: 120, height: 80, size: 12, bold: false, data: '{"key1":"value1","key2":"value2"}' },
    };
    updatePages(pages => {
      pages[curPage].elements.push({ ...base, ...defs[type], ...extra });
    });
    setSelId(id);
  }, [curPage, updatePages]);

  const addIcon = useCallback((name) => {
    const id = 'e' + (nextId++);
    updatePages(pages => {
      pages[curPage].elements.push({
        id, type: 'icon', name, size: 24,
        x: Math.round(DW/2 - 12), y: Math.round(DH/2 - 12),
      });
    });
    setSelId(id);
  }, [curPage, updatePages]);

  const deleteElement = useCallback(() => {
    if (!selId) return;
    updatePages(pages => {
      const els = pages[curPage].elements;
      const idx = els.findIndex(e => e.id === selId);
      if (idx >= 0) els.splice(idx, 1);
    });
    setSelId(null);
  }, [selId, curPage, updatePages]);

  // ─── Image upload ──────────────────────────────────────────────
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Scale to fit display while preserving aspect ratio
        let w = img.width, h = img.height;
        if (w > DW) { h = Math.round(h * DW / w); w = DW; }
        if (h > DH) { w = Math.round(w * DH / h); h = DH; }
        addElement('image', { data: reader.result, width: w, height: h, keepRatio: true });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [addElement]);

  // Replace image on selected element
  const handleReplaceImage = useCallback((e) => {
    const file = e.target.files[0];
    if (!file || !selId) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const el = getEl(selId);
        if (!el) return;
        // Scale to fit current element dimensions or display max
        const maxW = el.width || DW, maxH = el.height || DH;
        let w = img.width, h = img.height;
        if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
        if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
        updateElement(selId, { data: reader.result, width: w, height: h, pixels: undefined });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [selId, getEl, updateElement]);

  // ─── Pages management ──────────────────────────────────────────
  const addPage = useCallback(() => {
    updatePages(p => {
      p.push({ name: `Page ${p.length + 1}`, elements: [] });
    });
    setCurPage(pages.length);
    setSelId(null);
  }, [pages.length, updatePages]);

  const deletePage = useCallback((i) => {
    if (pages.length <= 1) return;
    updatePages(p => p.splice(i, 1));
    setCurPage(prev => prev >= pages.length - 1 ? pages.length - 2 : prev);
    setSelId(null);
  }, [pages.length, updatePages]);

  const renamePage = useCallback((i, name) => {
    updatePages(p => { p[i].name = name; });
  }, [updatePages]);

  // ─── Drag & Drop ───────────────────────────────────────────────
  const handleOverlayMouseDown = useCallback((e) => {
    // Find which element was clicked
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = (e.clientX - rect.left) / SCALE;
    const my = (e.clientY - rect.top) / SCALE;

    // Check resize handles first
    const resizeHandle = e.target.dataset?.resize;
    const targetId = e.target.dataset?.id || e.target.closest?.('[data-id]')?.dataset?.id;
    if (resizeHandle && targetId) {
      e.preventDefault();
      e.stopPropagation();
      const el = getEl(targetId);
      if (!el) return;
      setSelId(targetId);
      dragRef.current = {
        id: targetId, startX: e.clientX, startY: e.clientY,
        origX: el.x, origY: el.y,
        origW: el.width || 60, origH: el.height || 60,
        resize: resizeHandle,
      };
      return;
    }

    if (!targetId) {
      setSelId(null);
      return;
    }

    e.preventDefault();
    const el = getEl(targetId);
    if (!el) return;
    setSelId(targetId);
    dragRef.current = {
      id: targetId, startX: e.clientX, startY: e.clientY,
      origX: el.x, origY: el.y,
      origX2: el.x2, origY2: el.y2,
    };
  }, [getEl]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = (e.clientX - drag.startX) / SCALE;
      const dy = (e.clientY - drag.startY) / SCALE;

      if (drag.resize) {
        updatePages(pages => {
          const el = pages[curPage]?.elements?.find(e => e.id === drag.id);
          if (!el) return;
          const dir = drag.resize;
          let nw = drag.origW, nh = drag.origH, nx = drag.origX, ny = drag.origY;
          const ratio = drag.origW / drag.origH;
          const keepR = el.keepRatio !== false;

          if (dir.includes('e')) nw = Math.max(4, Math.round((drag.origW + dx) / SNAP) * SNAP);
          if (dir.includes('w')) { nw = Math.max(4, Math.round((drag.origW - dx) / SNAP) * SNAP); nx = drag.origX + drag.origW - nw; }
          if (dir.includes('s')) nh = Math.max(4, Math.round((drag.origH + dy) / SNAP) * SNAP);
          if (dir.includes('n')) { nh = Math.max(4, Math.round((drag.origH - dy) / SNAP) * SNAP); ny = drag.origY + drag.origH - nh; }

          if (keepR && dir.length === 2) {
            const rw = nw / drag.origW, rh = nh / drag.origH;
            if (Math.abs(rw - 1) > Math.abs(rh - 1)) {
              nh = Math.max(4, Math.round(nw / ratio / SNAP) * SNAP);
              if (dir.includes('n')) ny = drag.origY + drag.origH - nh;
            } else {
              nw = Math.max(4, Math.round(nh * ratio / SNAP) * SNAP);
              if (dir.includes('w')) nx = drag.origX + drag.origW - nw;
            }
          }

          el.width = nw; el.height = nh;
          el.x = Math.max(0, nx); el.y = Math.max(0, ny);
          delete el.pixels;
        });
        return;
      }

      updatePages(pages => {
        const el = pages[curPage]?.elements?.find(e => e.id === drag.id);
        if (!el) return;
        el.x = Math.max(0, Math.min(DW, Math.round((drag.origX + dx) / SNAP) * SNAP));
        el.y = Math.max(0, Math.min(DH, Math.round((drag.origY + dy) / SNAP) * SNAP));
        if (el.type === 'line' && drag.origX2 !== undefined) {
          el.x2 = Math.max(0, Math.min(DW, Math.round((drag.origX2 + dx) / SNAP) * SNAP));
          el.y2 = Math.max(0, Math.min(DH, Math.round((drag.origY2 + dy) / SNAP) * SNAP));
        }
      });
    };

    const handleMouseUp = () => { dragRef.current = null; };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [curPage, updatePages]);

  // ─── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selId) {
        deleteElement();
        return;
      }
      if (e.key === 'Escape') { setSelId(null); return; }
      if (!selId) return;
      const step = e.shiftKey ? 10 : 2;
      const move = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (move[e.key]) {
        e.preventDefault();
        const [dx, dy] = move[e.key];
        updateElement(selId, {});
        updatePages(pages => {
          const el = pages[curPage]?.elements?.find(e => e.id === selId);
          if (el) {
            el.x = Math.max(0, Math.min(DW, el.x + dx));
            el.y = Math.max(0, Math.min(DH, el.y + dy));
          }
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selId, curPage, deleteElement, updateElement, updatePages]);

  // ─── Save & Push ───────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!selectedType) return;
    setSaving(true);
    try {
      // Pre-render pixels for icons and images
      const pagesClone = JSON.parse(JSON.stringify(pages));
      for (const p of pagesClone) {
        for (const el of p.elements) {
          if (el.type === 'icon' && !el.pixels) {
            el.pixels = iconToPixels(el.name, el.size || 20);
          }
          if (el.type === 'image' && el.data && !el.pixels) {
            el.pixels = await imageToPixels(el.data, el.width || 60, el.height || 60, !!el.inverted);
          }
        }
      }
      const res = await einkDisplayApi.saveTemplate(selectedType.id, { pages: pagesClone, currentPage: curPage });
      setVersion(res.data.version);
      setDirty(false);
      enqueueSnackbar(t('EinkDesigner.saved'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('EinkDesigner.save_failed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [selectedType, pages, curPage, enqueueSnackbar, t]);

  const handlePush = useCallback(async () => {
    if (!selectedType) return;
    setPushing(true);
    try {
      await handleSave();
      await einkDisplayApi.push(selectedType.id);
      enqueueSnackbar(t('EinkDesigner.pushed'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('EinkDesigner.push_failed'), { variant: 'error' });
    } finally {
      setPushing(false);
    }
  }, [selectedType, handleSave, enqueueSnackbar, t]);

  // ─── Property setters ──────────────────────────────────────────
  const setProp = useCallback((key, val) => {
    if (!selId) return;
    updatePages(pages => {
      const el = pages[curPage]?.elements?.find(e => e.id === selId);
      if (!el) return;
      el[key] = val;
      if (el.type === 'icon' && (key === 'name' || key === 'size') && el.pixels) {
        el.pixels = iconToPixels(el.name, el.size || 20);
      }
      if (el.type === 'image' && (key === 'width' || key === 'height' || key === 'inverted')) {
        delete el.pixels;
      }
    });
  }, [selId, curPage, updatePages]);

  // ─── Selected element ──────────────────────────────────────────
  const selectedEl = selId ? getEl(selId) : null;

  // ─── Variable panel helpers ────────────────────────────────────
  const usedVars = useMemo(() => detectVariables(pages), [pages]);
  const customVars = usedVars.filter(v => !BUILTIN_VARS.includes(v));
  const usedBuiltins = usedVars.filter(v => BUILTIN_VARS.includes(v));

  // ─── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('EinkDesigner.title')}
        </Typography>

        <Autocomplete
          size="small"
          sx={{ width: 300 }}
          options={deviceTypes}
          loading={loadingTypes}
          getOptionLabel={(dt) => dt.FrontEndName || dt.friendlyName || `Type ${dt.id}`}
          value={selectedType}
          onChange={(_, v) => setSelectedType(v)}
          renderInput={(params) => (
            <TextField {...params} label={t('EinkDesigner.select_device_type')} size="small" />
          )}
        />

        {selectedType && (
          <Chip label={`v${version}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        )}

        <Box sx={{ flex: 1 }} />

        {dirty && <Chip label={t('EinkDesigner.unsaved')} color="warning" size="small" />}

        <Button
          variant="outlined"
          size="small"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!selectedType || saving}
        >
          {t('EinkDesigner.save')}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="success"
          startIcon={pushing ? <CircularProgress size={16} color="inherit" /> : <PushIcon />}
          onClick={handlePush}
          disabled={!selectedType || pushing}
        >
          {t('EinkDesigner.push_to_display')}
        </Button>
      </Box>

      {!selectedType ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="info" sx={{ maxWidth: 400 }}>
            {t('EinkDesigner.select_info')}
          </Alert>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* ─── Left Sidebar: Tools ─── */}
          <Paper
            elevation={0}
            sx={{
              width: 200, flexShrink: 0, p: 1.5, overflow: 'auto',
              borderRight: 1, borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
              {t('EinkDesigner.elements')}
            </Typography>
            {[
              { type: 'text', icon: <TextIcon fontSize="small" />, label: t('EinkDesigner.text') },
              { type: 'rect', icon: <RectIcon fontSize="small" />, label: t('EinkDesigner.rectangle') },
              { type: 'line', icon: <LineIcon fontSize="small" />, label: t('EinkDesigner.line') },
              { type: 'circle', icon: <CircleIcon fontSize="small" />, label: t('EinkDesigner.circle') },
              { type: 'json', icon: <JsonIcon fontSize="small" />, label: t('EinkDesigner.json_data') },
            ].map(({ type, icon, label }) => (
              <Button
                key={type}
                fullWidth
                size="small"
                startIcon={icon}
                onClick={() => addElement(type)}
                sx={{ justifyContent: 'flex-start', mb: 0.5, textTransform: 'none' }}
              >
                {label}
              </Button>
            ))}

            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: 1.5, mb: 0.5 }}>
              {t('EinkDesigner.icons')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
              {ICON_NAMES.map(name => (
                <Tooltip key={name} title={name} placement="top">
                  <IconButton
                    size="small"
                    onClick={() => addIcon(name)}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5 }}
                  >
                    <img
                      src={renderIconToDataURL(name, 18)}
                      alt={name}
                      width={18} height={18}
                      style={{ filter: 'invert(0.5)' }}
                    />
                  </IconButton>
                </Tooltip>
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: 1.5, mb: 0.5 }}>
              {t('EinkDesigner.image')}
            </Typography>
            <Button
              fullWidth size="small"
              startIcon={<ImageIcon fontSize="small" />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {t('EinkDesigner.upload_image')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
              {t('EinkDesigner.variables')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', mb: 0.5 }}>
              {t('EinkDesigner.variables_hint')}
            </Typography>
            {customVars.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 700 }}>{t('EinkDesigner.custom')}</Typography>
                {customVars.map(name => (
                  <TextField
                    key={name}
                    label={`{{${name}}}`}
                    size="small"
                    fullWidth
                    value={variables[name] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setVariables(prev => ({ ...prev, [name]: val }));
                    }}
                    sx={{ mb: 0.5, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                  />
                ))}
              </>
            )}
            {usedBuiltins.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.5, display: 'block', fontWeight: 700 }}>{t('EinkDesigner.in_use')}</Typography>
                {usedBuiltins.map(name => (
                  <Typography key={name} variant="caption" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                    <span style={{ color: '#3b82f6' }}>{`{{${name}}}`}</span> = {variables[name] || getLocalTimeVars()[name] || '...'}
                  </Typography>
                ))}
              </>
            )}
            {Object.entries(VAR_GROUPS).map(([group, vars]) => {
              const unusedVars = vars.filter(v => !usedVars.includes(v));
              if (unusedVars.length === 0) return null;
              const labels = { time: 'Time', system: 'System', sensor: 'Sensor', device: 'Device' };
              return (
                <Box key={group} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 700 }}>
                    {t(`EinkDesigner.var_group_${group}`) || labels[group]}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                    {unusedVars.map(name => (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          navigator.clipboard.writeText(`{{${name}}}`).catch(() => {});
                          enqueueSnackbar(`{{${name}}} copied`, { variant: 'info', autoHideDuration: 1500 });
                        }}
                        sx={{ fontSize: 10, height: 20, cursor: 'pointer', '& .MuiChip-label': { px: 0.5 } }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Paper>

          {/* ─── Canvas Area ─── */}
          <Box
            sx={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'action.hover', overflow: 'auto',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelId(null);
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: CW, height: CH,
                  border: '3px solid',
                  borderColor: 'text.primary',
                  cursor: 'crosshair',
                  overflow: 'hidden',
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={DW}
                  height={DH}
                  style={{
                    position: 'absolute', inset: 0,
                    width: CW, height: CH,
                    imageRendering: 'pixelated',
                  }}
                />
                {/* Interaction overlay */}
                <Box
                  ref={overlayRef}
                  onMouseDown={handleOverlayMouseDown}
                  sx={{
                    position: 'absolute', inset: 0,
                    width: CW, height: CH,
                  }}
                >
                  {hitBoxes.map(hb => (
                    <Box
                      key={hb.id}
                      data-id={hb.id}
                      sx={{
                        position: 'absolute',
                        left: hb.x * SCALE,
                        top: hb.y * SCALE,
                        width: hb.w * SCALE,
                        height: hb.h * SCALE,
                        cursor: 'move',
                        outline: hb.id === selId ? '2px solid #3b82f6' : 'none',
                        outlineOffset: 1,
                        '&:hover': { outline: hb.id !== selId ? '1px dashed rgba(59,130,246,0.4)' : undefined },
                      }}
                    >
                      {hb.id === selId && hb.resizable && (
                        ['nw','n','ne','w','e','sw','s','se'].map(dir => (
                          <Box
                            key={dir}
                            data-id={hb.id}
                            data-resize={dir}
                            sx={{
                              position: 'absolute',
                              width: 10, height: 10,
                              bgcolor: 'primary.main',
                              border: '1px solid white',
                              zIndex: 10,
                              ...(dir === 'nw' && { top: -5, left: -5, cursor: 'nwse-resize' }),
                              ...(dir === 'n' && { top: -5, left: '50%', ml: '-5px', cursor: 'ns-resize' }),
                              ...(dir === 'ne' && { top: -5, right: -5, cursor: 'nesw-resize' }),
                              ...(dir === 'w' && { top: '50%', left: -5, mt: '-5px', cursor: 'ew-resize' }),
                              ...(dir === 'e' && { top: '50%', right: -5, mt: '-5px', cursor: 'ew-resize' }),
                              ...(dir === 'sw' && { bottom: -5, left: -5, cursor: 'nesw-resize' }),
                              ...(dir === 's' && { bottom: -5, left: '50%', ml: '-5px', cursor: 'ns-resize' }),
                              ...(dir === 'se' && { bottom: -5, right: -5, cursor: 'nwse-resize' }),
                            }}
                          />
                        ))
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                {t('EinkDesigner.canvas_label', { width: DW, height: DH, scale: SCALE })}
              </Typography>
            </Box>
          </Box>

          {/* ─── Right Sidebar: Properties ─── */}
          <Paper
            elevation={0}
            sx={{
              width: 230, flexShrink: 0, p: 1.5, overflow: 'auto',
              borderLeft: 1, borderColor: 'divider',
            }}
          >
            {!selectedEl ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('EinkDesigner.select_element')}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t(`EinkDesigner.${selectedEl.type}`) || selectedEl.type}
                </Typography>

                {/* Position */}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    label="x" size="small" type="number" fullWidth
                    value={selectedEl.x || 0}
                    onChange={(e) => setProp('x', Number(e.target.value))}
                    sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                  />
                  <TextField
                    label="y" size="small" type="number" fullWidth
                    value={selectedEl.y || 0}
                    onChange={(e) => setProp('y', Number(e.target.value))}
                    sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                  />
                </Stack>

                {/* Type-specific properties */}
                {selectedEl.type === 'text' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      label="text" size="small" fullWidth
                      value={selectedEl.text || ''}
                      onChange={(e) => setProp('text', e.target.value)}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12 } }}
                    />
                    <TextField
                      label="size" size="small" type="number" fullWidth
                      value={selectedEl.size || 16}
                      onChange={(e) => setProp('size', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.bold} onChange={(e) => setProp('bold', e.target.checked)} />}
                      label={t('EinkDesigner.bold')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'icon' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: 12 }}>Icon</InputLabel>
                      <Select
                        label="Icon"
                        value={selectedEl.name || 'wifi'}
                        onChange={(e) => setProp('name', e.target.value)}
                        sx={{ fontSize: 12 }}
                      >
                        {ICON_NAMES.map(n => (
                          <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>{n}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="size" size="small" type="number" fullWidth
                      value={selectedEl.size || 20}
                      onChange={(e) => setProp('size', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const el = getEl(selId);
                        if (!el) return;
                        const initPixels = el.pixels || iconToPixels(el.name, el.size || 20);
                        setPixelEditorEl(el);
                        setPixelEditorInitPixels(initPixels);
                        setPixelEditorOpen(true);
                      }}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {selectedEl.pixels ? t('EinkDesigner.edit_pixels_custom') : t('EinkDesigner.edit_pixels')}
                    </Button>
                    {selectedEl.pixels && (
                      <Button
                        size="small" color="error" variant="text"
                        onClick={() => setProp('pixels', undefined)}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        {t('EinkDesigner.reset_to_icon')}
                      </Button>
                    )}
                  </Stack>
                )}

                {selectedEl.type === 'rect' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="width" size="small" type="number" fullWidth
                        value={selectedEl.width || 50}
                        onChange={(e) => setProp('width', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="height" size="small" type="number" fullWidth
                        value={selectedEl.height || 30}
                        onChange={(e) => setProp('height', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <TextField
                      label="strokeWidth" size="small" type="number" fullWidth
                      value={selectedEl.strokeWidth || 2}
                      onChange={(e) => setProp('strokeWidth', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.filled} onChange={(e) => setProp('filled', e.target.checked)} />}
                      label={t('EinkDesigner.filled')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'line' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="x2" size="small" type="number" fullWidth
                        value={selectedEl.x2 ?? (selectedEl.x || 0) + 50}
                        onChange={(e) => setProp('x2', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="y2" size="small" type="number" fullWidth
                        value={selectedEl.y2 ?? (selectedEl.y || 0)}
                        onChange={(e) => setProp('y2', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <TextField
                      label="strokeWidth" size="small" type="number" fullWidth
                      value={selectedEl.strokeWidth || 2}
                      onChange={(e) => setProp('strokeWidth', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'circle' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      label="radius" size="small" type="number" fullWidth
                      value={selectedEl.radius || 20}
                      onChange={(e) => setProp('radius', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <TextField
                      label="strokeWidth" size="small" type="number" fullWidth
                      value={selectedEl.strokeWidth || 2}
                      onChange={(e) => setProp('strokeWidth', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.filled} onChange={(e) => setProp('filled', e.target.checked)} />}
                      label={t('EinkDesigner.filled')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                  </Stack>
                )}

                {selectedEl.type === 'image' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="width" size="small" type="number" fullWidth
                        value={selectedEl.width || 60}
                        onChange={(e) => setProp('width', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="height" size="small" type="number" fullWidth
                        value={selectedEl.height || 60}
                        onChange={(e) => setProp('height', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={selectedEl.keepRatio !== false} onChange={(e) => setProp('keepRatio', e.target.checked)} />}
                      label={t('EinkDesigner.keep_ratio')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.inverted} onChange={(e) => setProp('inverted', e.target.checked)} />}
                      label={t('EinkDesigner.inverted')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                    <Button
                      size="small" variant="outlined"
                      onClick={() => replaceInputRef.current?.click()}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {t('EinkDesigner.replace_image')}
                    </Button>
                    <input
                      ref={replaceInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleReplaceImage}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        const el = getEl(selId);
                        if (!el) return;
                        let initPx = el.pixels;
                        if (!initPx && el.data) {
                          initPx = await imageToPixels(el.data, el.width || 60, el.height || 60, !!el.inverted);
                        }
                        if (!initPx) {
                          const w = el.width || 60, h = el.height || 60;
                          initPx = Array.from({ length: h }, () => '0'.repeat(w));
                        }
                        setPixelEditorEl(el);
                        setPixelEditorInitPixels(initPx);
                        setPixelEditorOpen(true);
                      }}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      {selectedEl.pixels ? t('EinkDesigner.edit_pixels_custom') : t('EinkDesigner.edit_pixels')}
                    </Button>
                    {selectedEl.pixels && (
                      <Button
                        size="small" color="error" variant="text"
                        onClick={() => setProp('pixels', undefined)}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        {t('EinkDesigner.reset_from_image')}
                      </Button>
                    )}
                  </Stack>
                )}

                {selectedEl.type === 'json' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="width" size="small" type="number" fullWidth
                        value={selectedEl.width || 120}
                        onChange={(e) => setProp('width', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                      <TextField
                        label="height" size="small" type="number" fullWidth
                        value={selectedEl.height || 80}
                        onChange={(e) => setProp('height', Number(e.target.value))}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      />
                    </Stack>
                    <TextField
                      label="size" size="small" type="number" fullWidth
                      value={selectedEl.size || 12}
                      onChange={(e) => setProp('size', Number(e.target.value))}
                      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!selectedEl.bold} onChange={(e) => setProp('bold', e.target.checked)} />}
                      label={t('EinkDesigner.bold')}
                      sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                    />
                    <TextField
                      label="JSON data" size="small" fullWidth multiline rows={4}
                      value={selectedEl.data || '{}'}
                      onChange={(e) => setProp('data', e.target.value)}
                      sx={{ '& .MuiInputBase-input': { fontSize: 11, fontFamily: 'monospace' } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                      {t('EinkDesigner.json_tip')}
                    </Typography>
                  </Stack>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={deleteElement}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  {t('EinkDesigner.delete_element')}
                </Button>
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* ─── Bottom Bar: Pages ─── */}
      {selectedType && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 0.5, borderTop: 1, borderColor: 'divider' }}>
          {pages.map((p, i) => (
            <React.Fragment key={i}>
              <Chip
                label={p.name}
                size="small"
                color={i === curPage ? 'primary' : 'default'}
                variant={i === curPage ? 'filled' : 'outlined'}
                onClick={() => { setCurPage(i); setSelId(null); }}
                onDoubleClick={() => {
                  const name = prompt(t('EinkDesigner.page_name_prompt'), p.name);
                  if (name) renamePage(i, name);
                }}
              />
              <Tooltip title={t('EinkDesigner.configure_buttons')}>
                <IconButton size="small" onClick={() => setButtonConfigPage(i)} sx={{ p: 0, ml: -0.5 }}>
                  <TuneIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              {pages.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deletePage(i)}
                  sx={{ p: 0, ml: -0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </React.Fragment>
          ))}
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon />}
            onClick={addPage}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {t('EinkDesigner.page')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            v{version}
          </Typography>
        </Box>
      )}

      {/* ─── Button Config Dialog ─── */}
      <ButtonConfigDialog
        open={buttonConfigPage !== null}
        page={buttonConfigPage !== null ? pages[buttonConfigPage] : null}
        pageIndex={buttonConfigPage}
        allPages={pages}
        t={t}
        onClose={() => setButtonConfigPage(null)}
        onApply={(buttons) => {
          if (buttonConfigPage !== null) {
            updatePages(p => { p[buttonConfigPage].buttons = buttons; });
          }
          setButtonConfigPage(null);
        }}
      />

      {/* ─── Pixel Editor Dialog ─── */}
      <PixelEditorDialog
        open={pixelEditorOpen}
        element={pixelEditorEl || getEl(selId)}
        initPixels={pixelEditorInitPixels}
        onClose={() => { setPixelEditorOpen(false); setPixelEditorInitPixels(null); }}
        onUpdate={(pixels) => {
          if (selId) {
            updateElement(selId, { pixels });
          }
          setPixelEditorOpen(false);
          setPixelEditorInitPixels(null);
        }}
      />
    </Box>
  );
}

// ─── Pixel Editor Dialog ──────────────────────────────────────────
function PixelEditorDialog({ open, element, initPixels, onClose, onUpdate }) {
  const [pixels, setPixels] = useState([]);
  const [painting, setPainting] = useState(false);
  const paintValRef = useRef('1');

  useEffect(() => {
    if (!open || !element) return;
    // Use explicitly passed initPixels first (avoids stale element ref)
    if (initPixels?.length) {
      setPixels(initPixels.map(r => r));
    } else if (element.pixels?.length) {
      setPixels(element.pixels.map(r => r));
    } else if (element.type === 'icon') {
      setPixels(iconToPixels(element.name, element.size || 20));
    } else {
      const w = element.width || 60, h = element.height || 60;
      setPixels(Array.from({ length: h }, () => '0'.repeat(w)));
    }
  }, [open, element, initPixels]);

  const togglePixel = useCallback((r, c) => {
    setPixels(prev => {
      const next = [...prev];
      const row = next[r];
      const newVal = paintValRef.current;
      next[r] = row.substring(0, c) + newVal + row.substring(c + 1);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    if (!element) return;
    if (element.type === 'icon') {
      setPixels(iconToPixels(element.name, element.size || 20));
    }
  }, [element]);

  const handleClear = useCallback(() => {
    if (!element) return;
    const pw = element.type === 'icon' ? (element.size || 20) : (element.width || 60);
    const ph = element.type === 'icon' ? (element.size || 20) : (element.height || 60);
    setPixels(Array.from({ length: ph }, () => '0'.repeat(pw)));
  }, [element]);

  const handleInvert = useCallback(() => {
    setPixels(prev => prev.map(row =>
      row.split('').map(c => c === '1' ? '0' : '1').join('')
    ));
  }, []);

  if (!element) return null;

  const pw = pixels[0]?.length || 1;
  const ph = pixels.length || 1;
  const cellSize = Math.max(4, Math.min(12, Math.floor(600 / Math.max(pw, ph))));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        {`${element.type === 'icon' ? element.name : 'Image'} (${pw}x${ph})`}
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" onClick={handleReset}>
            {element.type === 'icon' ? 'Reset' : 'Reset'}
          </Button>
          <Button size="small" variant="outlined" onClick={handleClear}>Clear</Button>
          <Button size="small" variant="outlined" onClick={handleInvert}>Invert</Button>
        </Stack>
        <Box
          sx={{
            display: 'inline-grid',
            gridTemplateColumns: `repeat(${pw}, ${cellSize}px)`,
            border: 1, borderColor: 'divider',
            cursor: 'crosshair',
            userSelect: 'none',
          }}
          onMouseLeave={() => setPainting(false)}
          onMouseUp={() => setPainting(false)}
        >
          {pixels.map((row, r) =>
            row.split('').map((c, col) => (
              <Box
                key={`${r}-${col}`}
                sx={{
                  width: cellSize, height: cellSize,
                  bgcolor: c === '1' ? '#000' : '#fff',
                  border: '0.5px solid',
                  borderColor: 'divider',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setPainting(true);
                  paintValRef.current = c === '1' ? '0' : '1';
                  togglePixel(r, col);
                }}
                onMouseEnter={() => {
                  if (painting) togglePixel(r, col);
                }}
              />
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={() => onUpdate(pixels)}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
